import { create } from 'zustand';
import { Projet, ProjetRequestDTO, StatutProjet } from '../types';
import { extractErrorMessage } from '../utils/errorUtils';
import projetService from '../services/projetService';

interface ProjetStore {
  projets: Projet[];
  isLoading: boolean;
  error: string | null;
  loadProjets: () => Promise<void>;
  addProjet: (projet: ProjetRequestDTO) => Promise<Projet>;
  updateProjet: (id: number, projet: Partial<ProjetRequestDTO>) => Promise<void>;
  deleteProjet: (id: number) => Promise<void>;
  getProjetById: (id: number) => Projet | undefined;
  getProjetsByClientId: (clientId: number) => Projet[];
  loadProjetsByClientId: (clientId: number) => Promise<void>;
  updateProjetStatus: (id: number, statut: StatutProjet) => Promise<void>;
  clearProjets: () => void;
}

export const useProjetStore = create<ProjetStore>((set, get) => ({
  projets: [],
  isLoading: false,
  error: null,

  // Charger tous les projets depuis l'API
  loadProjets: async () => {
    set({ isLoading: true, error: null });
    try {
      const projets = await projetService.getAll();
      set({ projets, isLoading: false });
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors du chargement des projets');
      set({ error: errorMessage, isLoading: false });
    }
  },

  // Ajouter un projet via l'API
  addProjet: async (projetData) => {
    set({ isLoading: true, error: null });
    try {
      const newProjet = await projetService.create(projetData);
      set((state) => ({
        projets: [...state.projets, newProjet],
        isLoading: false,
      }));
      return newProjet;
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la création du projet');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Mettre à jour un projet via l'API
  updateProjet: async (id, projetData) => {
    set({ isLoading: true, error: null });
    try {
      const updatedProjet = await projetService.update(id, projetData);
      set((state) => ({
        projets: state.projets.map((projet) =>
          projet.id === id ? updatedProjet : projet
        ),
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la mise à jour du projet');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Supprimer un projet via l'API
  deleteProjet: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await projetService.delete(id);
      set((state) => ({
        projets: state.projets.filter((projet) => projet.id !== id),
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la suppression du projet');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Récupérer un projet par ID (depuis le state local)
  getProjetById: (id) => {
    return get().projets.find((projet) => projet.id === id);
  },

  // Récupérer tous les projets d'un client (depuis le state local)
  getProjetsByClientId: (clientId) => {
    return get().projets.filter((projet) => projet.clientId === clientId);
  },

  // Charger les projets d'un client depuis l'API
  loadProjetsByClientId: async (clientId) => {
    set({ isLoading: true, error: null });
    try {
      const projets = await projetService.getByClientId(clientId);
      // Merge avec les projets existants pour éviter la perte de données
      set((state) => ({
        projets: [
          ...state.projets.filter(p => p.clientId !== clientId),
          ...projets
        ],
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors du chargement des projets du client');
      set({ error: errorMessage, isLoading: false });
    }
  },

  // Mettre à jour le statut d'un projet via l'API
  updateProjetStatus: async (id, statut) => {
    set({ isLoading: true, error: null });
    try {
      const updatedProjet = await projetService.updateStatus(id, statut);
      set((state) => ({
        projets: state.projets.map((projet) =>
          projet.id === id ? updatedProjet : projet
        ),
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la mise à jour du statut');
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  // Vider tous les projets (lors du logout)
  clearProjets: () => {
    set({ projets: [], isLoading: false, error: null });
  },
}));
